// supply 결과보고서의 영문 문구 조회·조립 (node 테스트가 가능하도록 cjs 로 분리).
// 소비처: raw-data/supply-2026.js, report.supply.mixin.js
//
// 원칙: 영문이 없는 항목은 **항목 단위로 국문 폴백**한다. 번역이 빠진 항목 하나 때문에 생성이 죽으면
// generatePDF 의 catch 가 삼켜 그 지점 이후가 통째로 잘린다 (2026-09-14 넥센 C-4-1).
// ⚠️ 이 파일은 babel 을 거치지 않는다(.cjs) — 옵셔널 체이닝(?.)·널 병합(??) 금지.
const textEn = require('./raw-data/supply_report_text_en.json');

// 영문 보고서면 textEn 의 path 값을, 없거나 다른 언어면 ko 를 그대로 돌려준다.
function localized (lang, ko) {
    if (lang !== 'en') return ko;
    let cur = textEn;
    for (let i = 2; i < arguments.length; i++) {
        cur = cur == null ? cur : cur[arguments[i]];
    }
    return typeof cur === 'string' && cur ? cur : ko;
}

// 등급 기준표 한 Code 분량 {A,B,C,D} — 기준이 없는 칸('-')은 그대로 '-'.
function localizedLevelDesc (lang, koDesc, code) {
    const out = {};
    ['A', 'B', 'C', 'D'].forEach(g => { out[g] = localized(lang, koDesc[g], 'levelDesc', code, g); });
    return out;
}

// 요약 쪽 굵은 안내 문장 (ESG Code 준수율 카드)
function complianceRateMsgEn (companyName, levelData) {
    const total = levelData.total;
    const data = levelData.processedTotalCodeData;
    let msg = `The ESG Code compliance rate of ${companyName} is ${total.total_compliance}% (${data.compliance}/${data.total}). `;
    if (data.D > 0) {
        msg += `For the ${data.D} Code(s) assessed as "Critical Laggard", efforts are needed to identify the key risks and make improvements.`;
    }
    return msg;
}

// 요약 쪽 평가 신뢰도 상자 설명
function assessmentRateMsgEn (companyName, processedSubData, reliability) {
    return 'Where a large number of "Not Known" responses are selected during the assessment, or where the quality of the responses is insufficient for the assessor to make a judgment, such responses are deemed invalid and the data quality of the assessment is determined to be low.'
        + `\nIn the case of ${companyName}, a valid assessment was not possible for ${processedSubData.unknown} of the ${processedSubData.total} questions assessed, and the data quality level has therefore been defined as "${reliability}".`;
}

// 코드별 쪽 판정 문장 앞부분. 뒤에 Code·등급별 판정 문장이 붙는다.
function codeComplianceHeadEn (companyName, codeName, gradeLabel) {
    return `The level of compliance of ${companyName} with the Code "${codeName}" was assessed as "${gradeLabel}". `;
}

// DB 의 분류·Code명 영문: results[].data 에는 국문 이름만 온다 (EV_COUNT recipe 가 _ea 를 버림).
// 같은 응답의 template_questions[] / sources.sub_data[] 에 실린 category{N}_question_name_ea 로 국문→영문 맵을 만든다.
function buildQuestionNameMap (reportData) {
    const map = {};
    const data = reportData || {};
    const rows = [].concat(data.template_questions || [], (data.sources && data.sources.sub_data) || []);
    rows.forEach(row => {
        if (!row) return;
        [1, 2, 3, 4].forEach(lv => {
            const ko = row[`category${lv}_question_name`];
            const en = row[`category${lv}_question_name_ea`];
            if (ko && en && !map[ko]) map[ko] = en;
        });
    });
    return map;
}

// 평가 요소 formula 가 `"X" == result` / `result == 'X'` 꼴의 단순 등식이면 비교 리터럴 X 를, 아니면 null 을 돌려준다.
// 아니오/모름 평가 요소는 이 리터럴로 판정한다. 넥센 신규 Code(A-1-6·B-4-1·C-4-1)는 `result=='아니오'`,
// `result=='모름 또는 확인할 수 없음'` 처럼 표기가 달라, 문자열 완전일치로 고르면 하위 평가 요소로 새어 들어가
// Appendix 표에 등급 없는 행이 생기고 원형 태그를 그리다 throw → 이후 렌더가 통째로 빠진다 (2026-09-21 확인).
// `"…" in structure[…]`(하위 평가 요소)·`depend_on …` 은 등식이 아니라 null.
const ANSWER_EQ = /^\s*(?:(['"])(.*?)\1\s*==\s*result|result\s*==\s*(['"])(.*?)\3)\s*$/;
function answerLiteral (formula) {
    const m = typeof formula === 'string' ? formula.match(ANSWER_EQ) : null;
    if (!m) return null;
    return m[2] !== undefined ? m[2] : m[4];
}
function isNoAnswerFormula (formula) {
    return answerLiteral(formula) === '아니오';
}
function isUnknownAnswerFormula (formula) {
    const lit = answerLiteral(formula);
    return typeof lit === 'string' && lit.indexOf('모름') === 0;
}

// prod DB 에 실제로 있는 아니오/모름 formula 표기 전수 (2026-09-21 조회). 리터럴은 문항 df_subs_init.sub_data_contents 의
// 선택지 value 에 묶인 값이라 DB 쪽을 고치지 않고 여기서 전부 알아본다. 새 표기가 발견되면 여기에 예시를 추가한다
// (테스트가 이 목록 전부를 판정하는지 고정한다).
const KNOWN_ANSWER_FORMULAS = {
    no: [
        '"아니오" == result',    // 2026-01 철강협회 이관분 300여 개 (예: ev 100007013)
        'result==\'아니오\'',      // 넥센 신규 Code A-1-6·B-4-1·C-4-1 (ev 100008321·325·329·333·337·341·345·349)
    ],
    unknown: [
        '"모름" == result',                    // 철강협회 이관분 (예: ev 100007014)
        'result==\'모름\'',                      // 넥센 신규 Code B-4-1·C-4-1 (ev 100008330·334·338·342·346·350)
        'result==\'모름 또는 확인할 수 없음\'',   // 넥센 A-1-6 — 그 문항의 선택지 value 자체가 이 문장 (ev 100008322·326)
    ],
};

module.exports = {
    KNOWN_ANSWER_FORMULAS,
    answerLiteral,
    isNoAnswerFormula,
    isUnknownAnswerFormula,
    localized,
    localizedLevelDesc,
    complianceRateMsgEn,
    assessmentRateMsgEn,
    codeComplianceHeadEn,
    buildQuestionNameMap,
};
